import ButtonV2, { getButtonClasses } from "components/elements/buttonV2"
import SocialIcon from "components/elements/socialIcon"
import Img from "gatsby-image"
import PropTypes from "prop-types"
import React from "react"
import { useInstagramPosts } from "services/graphQl/queries/instagramPosts"
import { toParagraphs } from "utils/plainText"
import "./section-social-follow-v2.scss"

const IconLinks = ({ links }) => (
  <ul className="section-social-follow-v2--links">
    {links.map(([name, href]) => (
      <li key={name}>
        <a
          aria-label={`${name} (opens in a new tab)`}
          href={href}
          rel="noopener noreferrer"
          target="_blank"
        >
          <SocialIcon name={name} />
        </a>
      </li>
    ))}
  </ul>
)

IconLinks.propTypes = {
  links: PropTypes.arrayOf(PropTypes.array).isRequired,
}

// One Instagram post from the feed (see useInstagramPosts), with its image
// saved at build time.
const InstagramPost = ({ caption, image, imageUrl, mediaType, permalink }) => (
  <li className="section-social-follow-v2--post">
    <a
      aria-label={`${
        caption ? caption.slice(0, 80) : "Instagram post"
      } (opens Instagram in a new tab)`}
      href={permalink}
      rel="noopener noreferrer"
      target="_blank"
    >
      {image?.fluid ? (
        <Img alt="" fluid={image.fluid} />
      ) : (
        <img alt="" loading="lazy" src={imageUrl} />
      )}
      {mediaType === "VIDEO" && (
        <span
          aria-hidden="true"
          className="section-social-follow-v2--post-badge"
        >
          <svg height="16" viewBox="0 0 24 24" width="16">
            <path d="M8 5.5v13l10.5-6.5z" fill="currentColor" />
          </svg>
        </span>
      )}
      {caption && (
        <span
          aria-hidden="true"
          className="section-social-follow-v2--post-caption"
        >
          {caption}
        </span>
      )}
    </a>
  </li>
)

InstagramPost.propTypes = {
  caption: PropTypes.string,
  image: PropTypes.object,
  imageUrl: PropTypes.string,
  mediaType: PropTypes.string,
  permalink: PropTypes.string,
}

const SectionSocialFollowV2 = ({
  facebookLink,
  image,
  instagramHandle,
  instagramLink,
  linkedinLink,
  showInstagramFeed,
  text,
  tiktokLink,
  title,
  youtubeLink,
}) => {
  const feed = useInstagramPosts()
  const posts = showInstagramFeed ? feed : []
  const links = [
    ["TikTok", tiktokLink],
    ["YouTube", youtubeLink],
    ["Instagram", instagramLink],
    ["Facebook", facebookLink],
    ["LinkedIn", linkedinLink],
  ].filter(([, href]) => href)

  // With Instagram posts, show them as a feed and keep the icons for the
  // other platforms. Without posts (for example if the Instagram connection
  // lapses), fall back to the image and all the icons.
  if (posts.length > 0) {
    return (
      <section className="section-social-follow-v2 section-social-follow-v2--feed">
        <div className="container-v2">
          <div className="section-social-follow-v2--feed-header">
            <div>
              {title && (
                <h2 className="section-social-follow-v2--title">{title}</h2>
              )}
              {toParagraphs(text?.text).map(paragraph => (
                <p className="section-social-follow-v2--text" key={paragraph}>
                  {paragraph}
                </p>
              ))}
            </div>
            {instagramLink && (
              <p className="section-social-follow-v2--follow">
                <ButtonV2
                  className={getButtonClasses("Solid Blue", "small")}
                  href={instagramLink}
                  newWindow={true}
                  text={
                    instagramHandle
                      ? `Follow ${instagramHandle}`
                      : "Follow on Instagram"
                  }
                />
              </p>
            )}
          </div>
          <ul className="section-social-follow-v2--posts">
            {posts.slice(0, 6).map(post => (
              <InstagramPost key={post.id} {...post} />
            ))}
          </ul>
          {links.length > 1 && (
            <div className="section-social-follow-v2--more">
              <p className="section-social-follow-v2--more-label">
                More places to follow
              </p>
              <IconLinks
                links={links.filter(([name]) => name !== "Instagram")}
              />
            </div>
          )}
        </div>
      </section>
    )
  }

  return (
    <section className="section-social-follow-v2">
      <div className="container-v2">
        {image?.fluid && (
          <div className="section-social-follow-v2--image">
            <Img alt={image.description || ""} fluid={image.fluid} />
          </div>
        )}
        <div className="section-social-follow-v2--content">
          {title && (
            <h2 className="section-social-follow-v2--title">{title}</h2>
          )}
          {toParagraphs(text?.text).map(paragraph => (
            <p className="section-social-follow-v2--text" key={paragraph}>
              {paragraph}
            </p>
          ))}
          {links.length > 0 && <IconLinks links={links} />}
        </div>
      </div>
    </section>
  )
}

SectionSocialFollowV2.propTypes = {
  facebookLink: PropTypes.string,
  image: PropTypes.object,
  instagramHandle: PropTypes.string,
  instagramLink: PropTypes.string,
  linkedinLink: PropTypes.string,
  showInstagramFeed: PropTypes.bool,
  text: PropTypes.shape({ text: PropTypes.string }),
  tiktokLink: PropTypes.string,
  title: PropTypes.string,
  youtubeLink: PropTypes.string,
}

export default SectionSocialFollowV2
